import * as React from 'react';

import markOutlineRaw from '../../../public/totem-mark-outline.svg?raw';
import markRaw from '../../../public/totem-mark.svg?raw';
import wordmarkRaw from '../../../public/totem-wordmark.svg?raw';

import { cn } from '@/shared/lib/cn';

/**
 * Totem brand marks — the SINGLE source of truth is the SVG files under `apps/web/public`
 * (also served directly as favicon / ticket assets). We `?raw`-import them and inline the
 * markup so the marks stay recolorable (`currentColor` + theme), which a plain `<img>` can't do.
 *
 * - `TotemMark`        two-tone mascot from `totem-mark.svg`: body recolored to `currentColor`,
 *                      facial detail to `--totem-detail` (default white). Use as `text-primary`
 *                      / `text-muted-foreground/40`.
 * - `TotemMarkOutline` filled mascot from `totem-mark-outline.svg`, rendered as-authored
 *                      (blue body + white line-art). Used on the navy rail / login hero.
 * - `TotemWordmark`    the "tótem" logotype from `totem-wordmark.svg`, recolored to `currentColor`.
 */

const BRAND_BLUE = /fill="#194D9D"/g;
const BRAND_WHITE = /fill="(?:white|#ffffff)"/gi;

/** Inner markup of an `<svg>` string (drops the outer `<svg …>` / `</svg>` wrapper). */
function innerMarkup(raw: string): string {
  return raw
    .replace(/^[\s\S]*?<svg[^>]*>/, '')
    .replace(/<\/svg>\s*$/, '')
    .trim();
}

/** The `viewBox` declared on the source file (keeps geometry in lock-step with the asset). */
function viewBoxOf(raw: string): string {
  const match = /viewBox="([^"]*)"/.exec(raw);
  return match?.[1] ?? '0 0 0 0';
}

/** React's raw-HTML sink; isolates the mandated `__html` key from naming-convention lint. */
function rawHtml(html: string) {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- React dangerouslySetInnerHTML contract
  return { __html: html };
}

function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Two-tone mascot: body → currentColor, white facial detail → --totem-detail.
const MARK_INNER = innerMarkup(markRaw)
  .replace(BRAND_BLUE, 'fill="currentColor"')
  .replace(BRAND_WHITE, 'fill="var(--totem-detail, #ffffff)"');
const MARK_VIEWBOX = viewBoxOf(markRaw);

// Filled mascot, kept as-authored (hardcoded blue + white).
const MARK_OUTLINE_INNER = innerMarkup(markOutlineRaw);
const MARK_OUTLINE_VIEWBOX = viewBoxOf(markOutlineRaw);

// Wordmark: single-colour → currentColor.
const WORDMARK_INNER = innerMarkup(wordmarkRaw).replace(BRAND_BLUE, 'fill="currentColor"');
const WORDMARK_VIEWBOX = viewBoxOf(wordmarkRaw);

type MarkProps = React.SVGProps<SVGSVGElement> & { title?: string };

function svgAria(title?: string): { role: string; 'aria-label'?: string; 'aria-hidden'?: true } {
  return title
    ? { role: 'img', 'aria-label': title }
    : { role: 'presentation', 'aria-hidden': true };
}

function markHtml(inner: string, title?: string): string {
  return title ? `<title>${escapeXml(title)}</title>${inner}` : inner;
}

/** Two-tone mascot. Body = currentColor; detail = `--totem-detail` (default white). */
export function TotemMark({ title, className, ...props }: MarkProps): JSX.Element {
  return (
    <svg
      viewBox={MARK_VIEWBOX}
      className={cn('h-auto', className)}
      {...svgAria(title)}
      {...props}
      dangerouslySetInnerHTML={rawHtml(markHtml(MARK_INNER, title))}
    />
  );
}

/** Filled line-art mascot, rendered as authored in `totem-mark-outline.svg`. */
export function TotemMarkOutline({ title, className, ...props }: MarkProps): JSX.Element {
  return (
    <svg
      viewBox={MARK_OUTLINE_VIEWBOX}
      className={cn('h-auto', className)}
      {...svgAria(title)}
      {...props}
      dangerouslySetInnerHTML={rawHtml(markHtml(MARK_OUTLINE_INNER, title))}
    />
  );
}

/** The "tótem" logotype (currentColor). */
export function TotemWordmark({ title, className, ...props }: MarkProps): JSX.Element {
  return (
    <svg
      viewBox={WORDMARK_VIEWBOX}
      className={cn('h-auto', className)}
      {...svgAria(title)}
      {...props}
      dangerouslySetInnerHTML={rawHtml(markHtml(WORDMARK_INNER, title))}
    />
  );
}
